/**
 * Legacy App URL utilities
 *
 * In development, the legacy AngularJS app runs on a different port (4220).
 * In production, both apps are on the same domain, so relative URLs work.
 */

/**
 * Get the base URL for the legacy app
 * - Development: http://localhost:4220
 * - Production: '' (relative, same domain)
 */
export function getLegacyBaseUrl(): string {
  if (import.meta.env.DEV) {
    return import.meta.env.VITE_LEGACY_APP_URL || 'http://localhost:4220';
  }
  return '';
}

/**
 * Build a full URL to a legacy app route
 * @param path - The path including hash (e.g., '/app#/accounts/contracts/123')
 */
export function getLegacyUrl(path: string): string {
  return `${getLegacyBaseUrl()}${path}`;
}

/**
 * Absolute origin of the legacy V1 app, for links that must survive the
 * split-domain production deployment (V2 on v2.atek.io, V1 on v1.atek.io).
 * A relative /app# link opened from v2.atek.io hits the React SPA fallback
 * and renders its 404 page (QUAL-5954).
 */
export function getLegacyAppOrigin(): string {
  return import.meta.env.VITE_LEGACY_APP_URL
    || (import.meta.env.DEV ? 'http://localhost:4220' : 'https://v1.atek.io');
}

/**
 * Common legacy route builders
 */
export const legacyRoutes = {
  // ERP
  contract: (id: string) => getLegacyUrl(`/app#/erp/contracts/${id}`),
  invoice: (id: string) => getLegacyUrl(`/app#/invoices?id=${id}`),
  quote: (id: string) => getLegacyUrl(`/app#/quotes?id=${id}`),
  site: (id: string) => getLegacyUrl(`/app#/sites?id=${id}`),
  user: (id: string) => getLegacyUrl(`/app#/allusers?id=${id}`),
  organisation: (id: string) => getLegacyUrl(`/app#/atek/organisations?id=${id}`),

  // Operations
  ticket: (id: string) => getLegacyUrl(`/app#/tickets?ticket=${id}`),
  ticketsByOrg: (orgId: string) => getLegacyUrl(`/app#/tickets?organisation=${orgId}`),
  createTicket: (orgId: string) => getLegacyUrl(`/app#/tickets/create?organisation=${orgId}`),
  sensor: (id: string) => getLegacyUrl(`/app#/ops-transmitters/${id}`),
  stream: (mongoId: string) => getLegacyUrl(`/app#/streams?id=${mongoId}`),
  // Certificate review (QUAL-5954): signing happens on the V1 calibration
  // record page, and this link is clicked from v2.atek.io — so it must carry
  // the absolute V1 origin, not a same-origin relative path.
  calibrationRecord: (mongoId: string) => `${getLegacyAppOrigin()}/app#/calibrationrecords?id=${mongoId}`,
  installationQualification: (id?: string) => (
    `${getLegacyAppOrigin()}/app#/installationqualification${id ? `?id=${id}` : ''}`
  ),

  // Intelligence
  orgIntelligence: (id: string) => getLegacyUrl(`/app#/intelligence/organisation/${id}`),

  // General
  dashboard: () => getLegacyUrl('/app#/atekdash'),
  sensors: () => getLegacyUrl('/app#/ops-transmitters'),
  alerts: () => getLegacyUrl('/app#/alerts'),
  erp: () => getLegacyUrl('/app#/atek/organisations'),
};
